import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, AlertCircle, Info, X } from "lucide-react";

/**
 * ─── Toast ────────────────────────────────────────────────────────────────
 * Non-blocking save/error confirmation (PDR §8 · P2).
 *
 *   const toast = useToast();
 *   toast.success("Saved.");
 *   toast.error("Could not save.", { description: "Retry in a minute." });
 *   toast.info("6 rows updated.");
 *
 * Toasts stack top-right, auto-dismiss after 4s, ESC dismisses the top.
 * `role="status"` for info/success, `role="alert"` for errors.
 */
const Ctx = createContext(null);
export function useToast() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useToast() must be used inside <ToastProvider>");
  return v;
}

let __idSeq = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    setToasts(t => t.filter(x => x.id !== id));
    if (timers.current[id]) { clearTimeout(timers.current[id]); delete timers.current[id]; }
  }, []);

  const push = useCallback((tone, message, opts = {}) => {
    const id = ++__idSeq;
    const t = { id, tone, message, description: opts.description, duration: opts.duration ?? 4000 };
    setToasts(prev => [...prev, t]);
    if (t.duration > 0) {
      timers.current[id] = setTimeout(() => dismiss(id), t.duration);
    }
    return id;
  }, [dismiss]);

  const api = {
    success: (m, o) => push("success", m, o),
    error:   (m, o) => push("danger",  m, { ...o, duration: o?.duration ?? 6000 }),
    info:    (m, o) => push("info",    m, o),
    warning: (m, o) => push("warning", m, o),
    dismiss,
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && toasts.length) dismiss(toasts[toasts.length - 1].id);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toasts, dismiss]);

  return (
    <Ctx.Provider value={api}>
      {children}
      {typeof document !== "undefined" && createPortal(
        <div className="ds-toast-stack" aria-live="polite" data-testid="toast-stack">
          {toasts.map(t => (
            <ToastRow key={t.id} toast={t} onClose={() => dismiss(t.id)} />
          ))}
        </div>,
        document.body
      )}
    </Ctx.Provider>
  );
}

function ToastRow({ toast, onClose }) {
  const Icon =
    toast.tone === "success" ? CheckCircle2 :
    toast.tone === "danger"  ? AlertCircle  :
    toast.tone === "warning" ? AlertCircle  : Info;
  const role = toast.tone === "danger" || toast.tone === "warning" ? "alert" : "status";
  return (
    <div className={`ds-toast ds-toast--${toast.tone}`} role={role} data-testid={`toast-${toast.id}`}>
      <Icon size={16} strokeWidth={2.2} className="ds-toast__icon" aria-hidden />
      <div className="ds-toast__body">
        <div className="ds-toast__msg">{toast.message}</div>
        {toast.description && <div className="ds-toast__desc">{toast.description}</div>}
      </div>
      <button className="ds-toast__close" onClick={onClose} aria-label="Dismiss">
        <X size={12} strokeWidth={2.4} />
      </button>
    </div>
  );
}
